import { useId, useState } from "react";
import { cn } from "@/lib/utils";

// Geometry is the brand loader asset (otus-loader.svg), unchanged. Fins are in
// clockwise order, starting at the top.
const FIN_PATHS = [
  "M25.9406 10.4413C25.5916 10.4171 25.2393 10.4049 24.8842 10.4049C20.9695 10.4049 17.4038 11.8991 14.7263 14.3483L10.6103 7.21907C14.5437 4.13657 19.4993 2.29883 24.8842 2.29883C26.741 2.29883 28.5468 2.51734 30.2773 2.93006L25.9406 10.4413Z",
  "M38.4366 18.8968C36.664 15.2429 33.4573 12.4138 29.548 11.1412L33.6599 4.01924C40.1133 6.66355 45.108 12.1369 47.1015 18.8968L38.4366 18.8968Z",
  "M39.6212 22.3708C39.829 23.3676 39.9382 24.4005 39.9382 25.4589C39.9382 28.62 38.9639 31.5536 37.2992 33.9756L41.6232 41.465C45.6009 37.3064 48.0442 31.668 48.0442 25.4589C48.0442 24.4119 47.9748 23.3811 47.8402 22.3708H39.6212Z",
  "M23.5828 40.4575C24.0117 40.4942 24.4458 40.5129 24.8842 40.5129C28.7171 40.5129 32.2155 39.0805 34.8731 36.7217L38.9808 43.8363C35.0762 46.8358 30.1884 48.619 24.8842 48.619C22.947 48.619 21.0653 48.3811 19.2668 47.933L23.5828 40.4575Z",
  "M11.3318 32.0209C13.0717 35.6078 16.1938 38.3999 20.0058 39.7049L15.9021 46.8128C9.5486 44.1371 4.63901 38.7086 2.66686 32.0209H11.3318Z",
  "M12.3308 17.1471C10.7506 19.5289 9.83014 22.3864 9.83014 25.4589C9.83014 26.5173 9.93937 27.5502 10.1472 28.5469H1.92819C1.7936 27.5367 1.72412 26.5059 1.72412 25.4589C1.72412 19.3332 4.10228 13.763 7.98569 9.62118L12.3308 17.1471Z",
] as const;

const ARROW_PATH =
  "M17.7839 33.3227L17.2415 32.826L24.873 15.6667L32.5046 32.826L31.9622 33.3227L24.873 30.297L17.7839 33.3227Z";

// One loop is LOOP_MS, one fin per FIN_SLOT_MS. Keep in step with the
// `otus-loader-fin` keyframes in index.css, which hold a fin lit for its slot
// and fade it back over the next 100ms.
const LOOP_MS = 900;
const FIN_SLOT_MS = 150;

interface OtusLoaderProps {
  /** Width and height of the mark in px. */
  size?: number;
  /** Cover the viewport with the mark centered on the page background. */
  fullScreen?: boolean;
  /** Accessible name announced for the loading region. */
  label?: string;
  className?: string;
}

/**
 * The branded loader: six fins light in turn, clockwise from the top, around a
 * static arrow. See otus-loader.spec.md.
 */
export function OtusLoader({
  size = 80,
  fullScreen = false,
  label = "Loading",
  className,
}: OtusLoaderProps) {
  // Gradient ids are document-global, so two loaders on one page would
  // otherwise resolve each other's `url(#…)`. useId's punctuation is dropped
  // to keep the id safe inside `url(#…)`.
  const uid = `otus-loader-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  // Start the loop where the page clock already is, not at the top fin. A
  // loader that replaces another (the sign-in hold handing over to the projects
  // page) then carries on from the same fin instead of restarting.
  const [phaseMs] = useState(() => performance.now() % LOOP_MS);

  return (
    <div
      role="status"
      aria-label={label}
      className={cn(
        fullScreen
          ? "fixed inset-0 flex items-center justify-center bg-background"
          : "inline-flex",
        className,
      )}
    >
      <svg
        viewBox="0.884 1.459 48 48"
        width={size}
        height={size}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient
            id={`${uid}-mint`}
            gradientUnits="userSpaceOnUse"
            x1="1.72"
            y1="0"
            x2="48.05"
            y2="0"
          >
            <stop offset="0" stopColor="#71FFF1" />
            <stop offset="1" stopColor="#22E8D5" />
          </linearGradient>
          {/* The logo gradient at 60% brightness: the unlit ring. */}
          <linearGradient
            id={`${uid}-mint-dim`}
            gradientUnits="userSpaceOnUse"
            x1="1.72"
            y1="0"
            x2="48.05"
            y2="0"
          >
            <stop offset="0" stopColor="#449991" />
            <stop offset="1" stopColor="#148B80" />
          </linearGradient>
          <linearGradient
            id={`${uid}-arrow`}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="15.67"
            x2="0"
            y2="33.32"
          >
            <stop offset="0" stopColor="#0E8FFF" />
            <stop offset="1" stopColor="#067CDA" />
          </linearGradient>
        </defs>

        <g fill={`url(#${uid}-mint-dim)`}>
          {FIN_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>

        <g fill={`url(#${uid}-mint)`}>
          {FIN_PATHS.map((d, i) => (
            <path
              key={d}
              d={d}
              className="otus-loader-fin"
              style={{
                animationDelay: `${-(LOOP_MS - i * FIN_SLOT_MS) - phaseMs}ms`,
              }}
            />
          ))}
        </g>

        <path fill={`url(#${uid}-arrow)`} d={ARROW_PATH} />
      </svg>
    </div>
  );
}
